import React, { useEffect, useState } from "react";
import Application from "../../../apiResources/Application";
import BasePage from "../../BasePage";
import Pagination from "../../library/Pagination";
import { useDidMountEffect, usePagination } from "../../../hooks";
import ApplicationListFilterBar from "../../ApplicationListFilterBar";
import {
  ApplicationStatusGroups,
  EmployerApplicationsIndexFilter,
  EmployerApplicationsIndexStatus,
} from "../../../types";
import Container from "../../library/Container";
import Loader from "../../library/Loader";
import ApplicationIndexCard from "../../ApplicationIndexCard";

const Applications = () => {
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState<Application[]>([]);
  const pagination = usePagination();
  const [filters, setFilters] = useState<EmployerApplicationsIndexFilter>({
    status: ["all"],
  });
  const [totalApplications, setTotalApplications] = useState<number>();
  const [query, setQuery] = useState("");

  const fetchApplications = async () => {
    setLoading(true);

    const response = await Application.includes([
      "job",
      { engagement: { recruiter: ["currency"] } },
      "candidate",
    ])
      .where({
        feedbackOverdue: getFeedbackQueryParam(filters.status?.[0]),
        status: getStatusQueryParam(filters.status?.[0]),
        by_candidate_full_name_or_job_title: { match: query },
      })
      .page(pagination.page)
      .per(pagination.per)
      .order({ updated_at: "desc" })
      .stats({ total: "count" })
      .all();

    setApplications(response.data);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    setTotalApplications(response.meta.stats.total.count);
    setLoading(false);
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  useDidMountEffect(() => {
    fetchApplications();
  }, [filters, pagination.page, query]);

  useDidMountEffect(() => {
    pagination.setPage(1);
  }, [filters, query]);

  return (
    <BasePage title="Candidate Applications">
      <Container>
        <ApplicationListFilterBar
          initialQuery={query}
          onQueryChange={setQuery}
          filters={filters}
          onFiltersChange={setFilters}
        />

        {loading && <Loader text="Loading candidates..." />}

        {!loading && !applications.length && (
          <div>Sorry, you have no candidates that match that search.</div>
        )}

        {!loading && Boolean(applications.length) && (
          <div id="candidates-list">
            {applications.map((application) => (
              <ApplicationIndexCard
                key={application.id}
                application={application}
              />
            ))}
            <Pagination pagination={pagination} total={totalApplications} />
          </div>
        )}
      </Container>
    </BasePage>
  );
};

function getFeedbackQueryParam(status: EmployerApplicationsIndexStatus) {
  // It's important to return undefined and not false here as we want to ignore
  // whether the feedback is overdue or not. False would mean we only get
  // applications that don't have overdue feedback.
  return status === "feedback_overdue" ? true : undefined;
}

function getStatusQueryParam(status: EmployerApplicationsIndexStatus) {
  if (status === "awaiting_feedback") {
    return ApplicationStatusGroups.feedback;
  }

  if (status === "interviewing") {
    return ApplicationStatusGroups.interviewing;
  }

  if (status === "rejected") {
    return ApplicationStatusGroups.rejected;
  }

  return [];
}

export default Applications;
